import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Radar, Search as SearchIcon, Swords, Trophy, DollarSign, TrendingUp,
  Wallet, RefreshCw, Loader2, CalendarClock, ArrowRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api, brl, fmtDate, errMsg } from "@/lib/api";

const KPIS = [
  { key: "novas_hoje", label: "Novas hoje", icon: Sparkles, color: "text-sky-600", bg: "bg-sky-50" },
  { key: "compativeis", label: "Compatíveis (≥60%)", icon: Radar, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "em_analise", label: "Em análise", icon: SearchIcon, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "em_disputa", label: "Em disputa", icon: Swords, color: "text-orange-600", bg: "bg-orange-50" },
  { key: "ganhas", label: "Licitações ganhas", icon: Trophy, color: "text-emerald-600", bg: "bg-emerald-50" },
];
const MONEY = [
  { key: "valor_disputa", label: "Valor em disputa", icon: Swords },
  { key: "valor_ganho", label: "Valor ganho", icon: Trophy },
  { key: "faturamento", label: "Faturamento", icon: DollarSign },
  { key: "lucro", label: "Lucro", icon: TrendingUp },
  { key: "saldo_financeiro", label: "Saldo financeiro", icon: Wallet },
];
const PIE_COLORS = ["#059669", "#e11d48"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/radar/sync", { ufs: ["RN"] });
      toast.info("Sincronizando PNCP (RN)… pode levar ~1 min");
      const poll = setInterval(async () => {
        try {
          const { data: st } = await api.get("/radar/sync-status");
          if (!st.running) {
            clearInterval(poll);
            setSyncing(false);
            toast.success(`Radar sincronizado: ${st.imported || 0} novas, ${st.updated || 0} atualizadas`);
            load();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(errMsg(e));
      setSyncing(false);
    }
  };

  if (!data) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>;
  const k = data.kpis;

  return (
    <div className="animate-fade-up space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Bom dia, PRO INVICTA.</h1>
          <p className="text-sm text-slate-500">Visão geral do seu centro de comando — {data.kpis.total_radar} oportunidades no radar.</p>
        </div>
        <button data-testid="dashboard-sync-btn" onClick={sync} disabled={syncing}
          className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Sincronizar PNCP
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {KPIS.map((kpi) => (
          <div key={kpi.key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
            <div className={`mb-3 grid h-9 w-9 place-items-center rounded-lg ${kpi.bg}`}>
              <kpi.icon className={`h-[18px] w-[18px] ${kpi.color}`} />
            </div>
            <div data-testid={`kpi-${kpi.key}`} className="font-display text-2xl font-bold text-slate-900">{k[kpi.key]}</div>
            <div className="text-xs font-medium text-slate-500">{kpi.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {MONEY.map((m) => (
          <div key={m.key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-500">
              <m.icon className="h-4 w-4 text-slate-400" /> {m.label}
            </div>
            <div data-testid={`kpi-${m.key}`} className="font-display text-lg font-bold text-slate-900">{brl(k[m.key])}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="mb-4 font-display text-base font-semibold text-slate-800">Oportunidades por estado (UF)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.charts.por_uf}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#0284C7" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-display text-base font-semibold text-slate-800">Ganhos x Perdas</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.charts.ganhos_perdas} dataKey="value" nameKey="label" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {data.charts.ganhos_perdas.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % 2]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex justify-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />Ganhas</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-600" />Perdidas</span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-display text-base font-semibold text-slate-800">Oportunidades por mês</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.charts.por_mes}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0284C7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0284C7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#0284C7" fill="url(#g1)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-display text-base font-semibold text-slate-800">Fluxo de caixa</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[
              { label: "Entradas", value: data.charts.fluxo.entradas },
              { label: "Saídas", value: data.charts.fluxo.saidas },
            ]}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <Tooltip formatter={(v) => brl(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                <Cell fill="#10b981" /><Cell fill="#ef4444" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-sky-600" />
            <h3 className="font-display text-base font-semibold text-slate-800">Próximos prazos</h3>
          </div>
          <div className="space-y-2">
            {data.prazos.length === 0 && <p className="text-sm text-slate-400">Salve oportunidades no pipeline para acompanhar prazos.</p>}
            {data.prazos.map((p) => (
              <button key={p.id} onClick={() => navigate("/radar")} className="flex w-full items-center gap-3 rounded-lg border border-slate-100 p-2.5 text-left hover:bg-slate-50">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-amber-50 text-xs font-bold text-amber-700">{p.uf}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium text-slate-700">{p.objeto}</div>
                  <div className="text-[11px] text-slate-400">{fmtDate(p.data_encerramento_proposta)}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
